import { useEffect } from 'react';
import { LogOut } from 'lucide-react';
import { createRoute, redirect } from '@tanstack/react-router';
import { rootRoute } from './root';
import { authClient, getSession, devSignOut, DEV_AUTH_BYPASS } from '@/lib/auth-client';
import { startRumForUser, stopRum, reportRenderError } from '@/lib/rum';
import { Button } from '@/components/ui/button';
import { consoleAccess } from '@/lib/console-admin';
import { RouteError } from '@/admin/ConsoleRouteError';
import { PermissionProvider, useHasPermission } from '@/lib/permissions';
import { Board } from '@/admin/Board';
import { ConsoleNav } from '@/admin/ConsoleNav';
import { ConsoleSettingsMenu } from '@/admin/ConsoleSettingsMenu';
import { DeployBanner } from '@/admin/DeployBanner';

// The operator console: a drag-and-drop board over the dialer's entities
// (clients/pools/contacts/SDRs/caller IDs) so an admin can seed and manage the
// dialer without IEx. It talks to the BFF's admin-gated /bff/admin/* surface
// (admin/api.ts), which proxies the core. Session guard like the cockpit plus a
// role check; the JWT is sent as a REST bearer (not the WS ?token=).

// Gated at the call site so a control nobody may use never mounts, and never fires the
// settings read behind it.
function ConsoleActions() {
  return useHasPermission('console:admin') ? <ConsoleSettingsMenu /> : null;
}

function Console() {
  const { userEmail, role } = consoleRoute.useRouteContext();

  const onSignOut = () => {
    stopRum(); // stop recording before we tear down / navigate away
    if (DEV_AUTH_BYPASS) {
      devSignOut();
      window.location.href = '/login';
      return;
    }
    void authClient.signOut({
      fetchOptions: {
        onSuccess: () => {
          window.location.href = '/login';
        },
      },
    });
  };

  return (
    <PermissionProvider role={role}>
      <div className="mx-auto max-w-[1400px] p-6">
        <DeployBanner />

        {/* The Board renders the page header; the console passes its identity, the
          page strip and its own controls into it, so navigation and board actions
          are told apart by where they sit rather than by reading the labels. */}
        <Board
          title={
            <>
              <h1 className="text-lg font-semibold">Maestro · Console</h1>
              <p className="text-muted-foreground text-sm">{userEmail}</p>
            </>
          }
          pages={<ConsoleNav />}
          nav={<ConsoleActions />}
          signOut={
            <Button variant="outline" size="sm" onClick={onSignOut}>
              <LogOut />
              Sign out
            </Button>
          }
        />
      </div>
    </PermissionProvider>
  );
}

export const consoleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/console',
  // No session → login (same guard as the cockpit); signed in without console access →
  // cockpit. The role comes off the SAME JWT the BFF's gate checks, so the UI redirect
  // and the server 403 never disagree. This only spares people a board that would 403
  // on every fetch — the BFF is the real gate.
  beforeLoad: async () => {
    const { data } = await getSession();
    if (!data) throw redirect({ to: '/login' });
    const { allowed, role } = await consoleAccess();
    if (!allowed) throw redirect({ to: '/' });
    // Before the first render rather than on mount, so a component that throws
    // immediately is still recorded. See the cockpit's gate for the reasoning.
    startRumForUser(data.user.email);
    return { userEmail: data.user.email, role };
  },
  component: Console,
  // Catches the gate above rejecting rather than redirecting — the session read
  // is unwrapped, so an unreachable auth path throws — as well as a render
  // failure. The option types `onCatch` for one argument despite the runtime
  // offering a second.
  errorComponent: RouteError,
  onCatch: (error) => reportRenderError(error, 'route:console'),
});
